import React, { useEffect, useMemo, useState, lazy } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import SearchBox from '../../components/FlowBuilder/Common/SearchBox/index.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { useSnackBar } from '../../components/FlowBuilder/Common/SnackBar/index.tsx';
// import { Sms } from '../../../interfaces/Sms.interface.ts'; // Assuming you have a similar interface setup
import APIService from '../../Services/APIService';
import { Apis } from '../../Services/app.constants';
import Pagination from '../../components/FlowBuilder/Common/Pagination/index.tsx';
import { SmsActions } from '../../Services/_actions/SMS/sms.actions.ts';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import LongStringView from '../Shared/LongStringView/index.tsx';
import useDebounce from '../Shared/Delay/index.tsx';
import LoadingDisableWrapper from '../Shared/LoadingDisableWrapper/index.tsx';
import SMSDashboardHelp from '../Shared/HelpFIles/SMSDashboardHelp.tsx';
import { useRecoilValue } from 'recoil';
import { ConstantActions } from '../../Services/_actions/constant.actions.js';
import { globalpermissions } from '../../Services/Permission.js';
import { authzContextAtom } from '../../authorization/authzState';
import { canAccess } from '../../authorization/canAccess';
import { ACCESS } from '../../constants/authz';

interface Sms {
  id: string;
  name: string;
  headers: string[];
  status: string;
  content: string;
  created_at: string;
}

const SmsList: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const [smsList, setSmsList] = useState<Sms[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filtersObj, setFiltersObj] = useState({
    search: '',
    page: 0,
    limit: 10,
    status: 'approved',
  });
  const actions = SmsActions();
  const debouncedSearch = useDebounce(filtersObj.search, 500);
  const constActions = ConstantActions();
  const authz = useRecoilValue(authzContextAtom);
  const canCreateSms = authz.isAuthzV2Enabled
    ? canAccess(authz, ACCESS.SMS_TEMPLATE_CREATE)
    : constActions.userHasPermission([globalpermissions?.SMSDashboard?.createSMS]);
  const canEditSms = authz.isAuthzV2Enabled
    ? canAccess(authz, ACCESS.SMS_TEMPLATE_UPDATE)
    : constActions.userHasPermission([globalpermissions?.SMSDashboard?.editSMS]);
  const canSendSms = !authz.isAuthzV2Enabled || canAccess(authz, ACCESS.SMS_TEMPLATE_SEND);

  const fetchSmsList = async (filtersObj) => {
    setIsLoading(true);
    try {
      const response = await actions.getSmsList({ ...filtersObj, page: filtersObj?.page + 1 });
      if (response.status === 200 && response?.data?.templates) {
        setSmsList(response.data.templates);
        setTotalRecords(response.data.totalrecords || 0);
      } else {
        showSnackBar('Failed to fetch SMS list', 'error');
      }
    } catch (error) {
      showSnackBar('An error occurred while fetching the SMS list', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSmsList({ ...filtersObj, search: debouncedSearch, page: 0 });
  }, [debouncedSearch]);

  const handleSearch = (searchValue: string) => {
    setFiltersObj({ ...filtersObj, search: searchValue, page: 0 });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setFiltersObj({ ...filtersObj, page: newPage });
    fetchSmsList({ ...filtersObj, page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10) || 10;
    setFiltersObj({ ...filtersObj, limit: value, page: 0 });
    fetchSmsList({ ...filtersObj, limit: value, page: 0 });
  };

  const handleEdit = (sms: Sms) => {
    navigate(`/r/sms/edit/${sms.id}`, { state: { sms } });
  };

  const handleSendSms = (sms: Sms) => {
    navigate('/r/sms/send', { state: { sms } });
  };

  const columns = useMemo<MRT_ColumnDef<Sms>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 50,
        muiTableHeadCellProps: {
          sx: {
            backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            fontFamily: 'system-ui',
            paddingLeft: '35px !important', // header padding here
          },
        },
        muiTableBodyCellProps: {
          sx: {
            paddingLeft: '35px !important',
          },
        },
      },
      { accessorKey: 'name', header: 'Name', size: 150 },
      {
        accessorKey: 'headers',
        header: 'Headers',
        size: 200,
        Cell: ({ cell }) => cell.getValue<string[]>().join(', '),
      },
      { accessorKey: 'status', header: 'Status', size: 100 },
      {
        accessorKey: 'content',
        header: 'Content',
        enableSorting: false,
        size: 300,
        Cell: ({ cell }) => <LongStringView longString={cell?.getValue() ?? ''} />,
      },
      { accessorKey: 'created_at', header: 'Created At', size: 150 },
      {
        accessorKey: 'actions',
        header: 'Actions',
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
          const open = Boolean(anchorEl);

          const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
          };

          const handleMenuClose = () => {
            setAnchorEl(null);
          };

          return (
            <>
              <IconButton onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                {canEditSms && (
                    <MenuItem
                      onClick={() => {
                        handleEdit(row.original);
                        handleMenuClose();
                      }}
                    >
                      Edit
                    </MenuItem>
                  )}
                {canSendSms && (
                  <MenuItem
                    onClick={() => {
                      handleSendSms(row.original);
                      handleMenuClose();
                    }}
                  >
                    Send
                  </MenuItem>
                )}
              </Menu>
            </>
          );
        },
      },
    ],
    [navigate, canEditSms, canSendSms]
  );
  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'SMS' },
  ];

  return (
    <Box
      sx={{
        padding: '5px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            SMS
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
          {canCreateSms && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => navigate('/r/sms/create')}
              >
                Add
              </Button>
            )}
          <SMSDashboardHelp />
        </Grid>
      </Grid>

      <Grid
        container
        sx={{ alignItems: 'center', paddingTop: '8px', paddingBottom: '8px' }}
        className="search-box-style"
      >
        <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center', pb: 1.5 }}>
          {/* <Grid item xs={12} md={3}> */}
          <LoadingDisableWrapper loading = {isLoading} >
          <SearchBox value={filtersObj?.search || ''} handleSearch={handleSearch} disabled = {isLoading} />
          </LoadingDisableWrapper>
          {/* </Grid> */}
        </Grid>
      </Grid>

      {/* {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : ( */}
      <MaterialReactTable
        columns={columns}
        data={smsList}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 100px)', width: '100%' } }}
        muiTableHeadCellProps={{
          sx: {
            backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            fontFamily: 'system-ui',
          },
        }}
        enableTopToolbar={false}
        state={{
          showColumnFilters: false,
          isLoading,
          // pagination,
          // sorting,
        }}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableStickyHeader={true}
        // manualSorting={true}
        initialState={{
          density: 'comfortable',
        }}
        enablePagination={false}
        manualPagination={false}
        // onSortingChange={setSorting}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
            <Pagination
              totalCount={totalRecords}
              rowsPerPage={filtersObj?.limit}
              page={filtersObj?.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      {/* )} */}
    </Box>
  );
};

export default SmsList;
